import * as z from "zod";

import { offsetPaginationSchema } from "@workspace/shared/schema";

import { multiValueQuerySchema } from "./shared";

export const surveyStatusSchema = z.enum(["draft", "complete"]);

export const listSurveysInputSchema = offsetPaginationSchema.extend({
  organizationId: z.string(),
  q: z.string().optional(),
  status: multiValueQuerySchema(surveyStatusSchema).optional(),
});

export type ListSurveysInput = z.infer<typeof listSurveysInputSchema>;

export const surveySummarySchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  status: surveyStatusSchema,
  surveyType: z.string().nullish(),
  clientName: z.string().nullish(),
  vesselName: z.string().nullish(),
  make: z.string().nullish(),
  model: z.string().nullish(),
  surveyDate: z.coerce.date().nullish(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const listSurveysResponseSchema = z.object({
  data: z.array(surveySummarySchema),
  total: z.number(),
});

export type ListSurveysResponse = z.infer<typeof listSurveysResponseSchema>;

export const createSurveyInputSchema = z.object({
  organizationId: z.string(),
  status: surveyStatusSchema.optional(),
  surveyType: z.string().optional(),
  clientName: z.string().optional(),
  vesselName: z.string().optional(),
  make: z.string().optional(),
  model: z.string().optional(),
  loa: z.string().optional(),
  beam: z.string().optional(),
  draft: z.string().optional(),
  year: z.coerce.number().int().optional(),
  hullMaterial: z.string().optional(),
  fuel: z.string().optional(),
  engine: z.string().optional(),
  vesselConfiguration: z
    .object({
      vesselType: z.array(z.string()).optional(),
      propulsionDrive: z.array(z.string()).optional(),
      engineConfiguration: z.array(z.string()).optional(),
      rig: z.array(z.string()).optional(),
      additionalFeatures: z.array(z.string()).optional(),
      accommodationSpace: z.array(z.string()).optional(),
    })
    .strict()
    .partial()
    .optional(),
  hin: z.string().optional(),
  mouldNumber: z.string().optional(),
  officialNumber: z.string().optional(),
  registeredTonnage: z.string().optional(),
  ssrNumber: z.string().optional(),
  location: z.string().optional(),
  surveyDate: z.coerce.date().optional(),
  afloatAshore: z.string().optional(),
  includeEquipment: z.boolean().optional(),
  insurancePurpose: z.boolean().optional(),
  surveyedAshoreLocation: z.string().optional(),
  surveyedAshoreDate: z.coerce.date().optional(),
  surveyedAfloatLocation: z.string().optional(),
  surveyedAfloatDate: z.coerce.date().optional(),
  weatherWindForce: z.coerce.number().int().optional(),
  weatherWindDirection: z.string().optional(),
  weatherSky: z.string().optional(),
  weatherPrecipitation: z.string().optional(),
  weatherTemperatureC: z.coerce.number().int().optional(),
  useCanonicalAreas: z.boolean().optional(),
});

export type CreateSurveyInput = z.infer<typeof createSurveyInputSchema>;

export const createSurveyResponseSchema = z.object({
  survey: surveySummarySchema,
});

export type CreateSurveyResponse = z.infer<typeof createSurveyResponseSchema>;

export const surveyNoteSchema = z.object({
  id: z.string(),
  surveyItemId: z.string(),
  actions: z.string().nullish(),
  findings: z.string().nullish(),
  notes: z.string().nullish(),
  recommendations: z.string().nullish(),
  recUrgency: z.string().nullish(),
  aiParagraph: z.string().nullish(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const attachmentSchema = z.object({
  id: z.string(),
  surveyItemId: z.string(),
  fileKey: z.string(),
  filename: z.string(),
  contentType: z.string().nullish(),
  size: z.number().nullish(),
  createdAt: z.coerce.date(),
});

export const surveyItemPhraseSnapshotSchema = z.object({
  id: z.string(),
  text: z.string(),
  scope: z.enum(["global", "org", "survey"]),
  sortOrder: z.number().optional(),
  actionPhraseId: z.string().nullish().optional(),
  subItemIds: z.array(z.string()).optional(),
  defaultSelected: z.boolean().optional(),
  supportsNegation: z.boolean().optional(),
  defaultNegation: z.string().nullish().optional(),
  supportsQualifier: z.boolean().optional(),
  qualifierPosition: z
    .enum(["before_finding", "after_no"])
    .nullish()
    .optional(),
});

const surveyItemEditorPhraseSnapshotSchema = surveyItemPhraseSnapshotSchema.extend({
  bucket: z.enum([
    "descriptor",
    "location",
    "action",
    "check",
    "finding",
    "disclaimer",
    "qualifier",
    "recommendation",
  ]),
});

export const surveyItemSnapshotSchema = z.object({
  actions: z.array(surveyItemPhraseSnapshotSchema).default([]),
  findings: z.array(surveyItemPhraseSnapshotSchema).default([]),
  descriptors: z.array(surveyItemPhraseSnapshotSchema).default([]),
  locations: z.array(surveyItemPhraseSnapshotSchema).default([]),
  disclaimers: z.array(surveyItemPhraseSnapshotSchema).default([]),
  checks: z.array(surveyItemPhraseSnapshotSchema).default([]),
  recommendations: z.array(surveyItemPhraseSnapshotSchema).default([]),
  qualifiers: z.array(surveyItemPhraseSnapshotSchema).default([]),
  editor: z
    .object({
      version: z.literal(1),
      blocks: z.array(z.unknown()).default([]),
      targetQuantitiesByTargetId: z
        .record(
          z.string(),
          z.object({
            quantityMode: z.enum(["default", "plural", "1", "2", "3", "custom"]),
            customQuantity: z.string(),
            instance: z.number().optional(),
          }),
        )
        .default({}),
      actionDefaultsApplied: z.boolean().default(false),
      surveyScopedPhrases: z.array(surveyItemEditorPhraseSnapshotSchema).optional(),
    })
    .nullish()
    .optional(),
});

export type SurveyItemSnapshot = z.infer<typeof surveyItemSnapshotSchema>;

export const surveyItemSchema = z.object({
  id: z.string(),
  surveyAreaId: z.string(),
  atomicItemId: z.string().nullish(),
  title: z.string(),
  sortOrder: z.number(),
  relevant: z.boolean(),
  actionsOptions: z.string().nullish(),
  findingsOptions: z.string().nullish(),
  recommendationsOptions: z.string().nullish(),
  snapshot: surveyItemSnapshotSchema.nullish(),
  generatedParagraph: z.string().nullish(),
  generationStatus: z.enum(["draft", "generating", "generated"]),
  generatedAt: z.coerce.date().nullish(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  notes: z.array(surveyNoteSchema).optional(),
  attachments: z.array(attachmentSchema).optional(),
});

export const surveyAreaSchema = z.object({
  id: z.string(),
  surveyId: z.string(),
  name: z.string(),
  sortOrder: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  items: z.array(surveyItemSchema).optional(),
});

export const surveyDetailSchema = surveySummarySchema.extend({
  loa: z.string().nullish(),
  beam: z.string().nullish(),
  draft: z.string().nullish(),
  year: z.number().nullish(),
  hullMaterial: z.string().nullish(),
  fuel: z.string().nullish(),
  engine: z.string().nullish(),
  vesselConfiguration: z
    .object({
      vesselType: z.array(z.string()).optional(),
      propulsionDrive: z.array(z.string()).optional(),
      engineConfiguration: z.array(z.string()).optional(),
      rig: z.array(z.string()).optional(),
      additionalFeatures: z.array(z.string()).optional(),
      accommodationSpace: z.array(z.string()).optional(),
    })
    .strict()
    .partial()
    .nullish(),
  vesselConfigChips: z.array(z.string()).default([]),
  hin: z.string().nullish(),
  mouldNumber: z.string().nullish(),
  officialNumber: z.string().nullish(),
  registeredTonnage: z.string().nullish(),
  ssrNumber: z.string().nullish(),
  location: z.string().nullish(),
  afloatAshore: z.string().nullish(),
  includeEquipment: z.boolean().nullish(),
  insurancePurpose: z.boolean().nullish(),
  surveyedAshoreLocation: z.string().nullish(),
  surveyedAshoreDate: z.coerce.date().nullish(),
  surveyedAfloatLocation: z.string().nullish(),
  surveyedAfloatDate: z.coerce.date().nullish(),
  weatherWindForce: z.number().nullish(),
  weatherWindDirection: z.string().nullish(),
  weatherSky: z.string().nullish(),
  weatherPrecipitation: z.string().nullish(),
  weatherTemperatureC: z.number().nullish(),
  libraryLastRefreshedAt: z.coerce.date().nullish(),
  snapshot: z.unknown().nullish(),
});

export const updateSurveyInputSchema = z.object({
  surveyId: z.string(),
  status: surveyStatusSchema.optional(),
  surveyType: z.string().nullish().optional(),
  vesselName: z.string().nullish().optional(),
  make: z.string().nullish().optional(),
  model: z.string().nullish().optional(),
  loa: z.string().nullish().optional(),
  beam: z.string().nullish().optional(),
  draft: z.string().nullish().optional(),
  year: z.coerce.number().int().nullish().optional(),
  hullMaterial: z.string().nullish().optional(),
  fuel: z.string().nullish().optional(),
  engine: z.string().nullish().optional(),
  vesselConfiguration: z
    .object({
      vesselType: z.array(z.string()).optional(),
      propulsionDrive: z.array(z.string()).optional(),
      engineConfiguration: z.array(z.string()).optional(),
      rig: z.array(z.string()).optional(),
      additionalFeatures: z.array(z.string()).optional(),
      accommodationSpace: z.array(z.string()).optional(),
    })
    .strict()
    .partial()
    .nullish()
    .optional(),
  vesselConfigChips: z.array(z.string()).optional(),
  hin: z.string().nullish().optional(),
  mouldNumber: z.string().nullish().optional(),
  officialNumber: z.string().nullish().optional(),
  registeredTonnage: z.string().nullish().optional(),
  ssrNumber: z.string().nullish().optional(),
  includeEquipment: z.boolean().nullish().optional(),
  insurancePurpose: z.boolean().nullish().optional(),
  surveyedAshoreLocation: z.string().nullish().optional(),
  surveyedAshoreDate: z.coerce.date().nullish().optional(),
  surveyedAfloatLocation: z.string().nullish().optional(),
  surveyedAfloatDate: z.coerce.date().nullish().optional(),
  weatherWindForce: z.coerce.number().int().nullish().optional(),
  weatherWindDirection: z.string().nullish().optional(),
  weatherSky: z.string().nullish().optional(),
  weatherPrecipitation: z.string().nullish().optional(),
  weatherTemperatureC: z.coerce.number().int().nullish().optional(),
  libraryLastRefreshedAt: z.coerce.date().nullish().optional(),
  snapshot: z.unknown().nullish().optional(),
});

export type UpdateSurveyInput = z.infer<typeof updateSurveyInputSchema>;

export const updateSurveyResponseSchema = z.object({
  ok: z.literal(true),
  surveyId: z.string(),
  updatedAt: z.coerce.date(),
});

export type UpdateSurveyResponse = z.infer<typeof updateSurveyResponseSchema>;

export const refreshSurveyItemOptionsInputSchema = z.object({
  surveyId: z.string(),
  organizationId: z.string(),
});

export type RefreshSurveyItemOptionsInput = z.infer<
  typeof refreshSurveyItemOptionsInputSchema
>;

export const refreshSurveyItemOptionsResponseSchema = z.object({
  updated: z.number(),
  snapshotsByItemId: z.record(z.string(), surveyItemSnapshotSchema),
});

export type RefreshSurveyItemOptionsResponse = z.infer<
  typeof refreshSurveyItemOptionsResponseSchema
>;

export const getSurveyInputSchema = z.object({
  view: z.enum(["full", "vessel-details"]).optional(),
});

export type GetSurveyInput = z.infer<typeof getSurveyInputSchema>;

export const getSurveyResponseSchema = z.object({
  survey: surveyDetailSchema,
  areas: z.array(surveyAreaSchema),
});

export type GetSurveyResponse = z.infer<typeof getSurveyResponseSchema>;

export const autoSelectSurveyStructureResponseSchema = z.object({
  areasIncluded: z.number(),
  areasExcluded: z.number(),
  itemsIncluded: z.number(),
  itemsExcluded: z.number(),
});

export type AutoSelectSurveyStructureResponse = z.infer<
  typeof autoSelectSurveyStructureResponseSchema
>;

export const createSurveyAreaInputSchema = z.object({
  surveyId: z.string(),
  name: z.string().min(1),
  sortOrder: z.number().optional(),
});

export type CreateSurveyAreaInput = z.infer<typeof createSurveyAreaInputSchema>;

export const createSurveyAreaResponseSchema = z.object({
  area: surveyAreaSchema,
});

export type CreateSurveyAreaResponse = z.infer<
  typeof createSurveyAreaResponseSchema
>;

export const createSurveyItemInputSchema = z.object({
  surveyId: z.string(),
  surveyAreaId: z.string(),
  atomicItemId: z.string().optional(),
  title: z.string().min(1),
  allowDuplicate: z.boolean().optional(),
  relevant: z.boolean().optional(),
  sortOrder: z.number().optional(),
  actionsOptions: z.string().optional(),
  findingsOptions: z.string().optional(),
  recommendationsOptions: z.string().optional(),
  actions: z.string().optional(),
  findings: z.string().optional(),
  notes: z.string().optional(),
  recommendations: z.string().optional(),
  recUrgency: z.string().optional(),
  aiParagraph: z.string().optional(),
});

export type CreateSurveyItemInput = z.infer<typeof createSurveyItemInputSchema>;

export const createSurveyItemResponseSchema = z.object({
  item: surveyItemSchema,
  note: surveyNoteSchema.nullish(),
});

export type CreateSurveyItemResponse = z.infer<
  typeof createSurveyItemResponseSchema
>;

export const createSurveyNoteInputSchema = z.object({
  surveyId: z.string(),
  surveyItemId: z.string(),
  actions: z.string().optional(),
  findings: z.string().optional(),
  notes: z.string().optional(),
  recommendations: z.string().optional(),
  recUrgency: z.string().optional(),
  aiParagraph: z.string().optional(),
});

export type CreateSurveyNoteInput = z.infer<typeof createSurveyNoteInputSchema>;

export const createSurveyNoteResponseSchema = z.object({
  note: surveyNoteSchema,
});

export type CreateSurveyNoteResponse = z.infer<
  typeof createSurveyNoteResponseSchema
>;

export const updateSurveyItemInputSchema = z.object({
  surveyId: z.string(),
  itemId: z.string(),
  snapshot: surveyItemSnapshotSchema.optional(),
  title: z.string().optional(),
  relevant: z.boolean().optional(),
  sortOrder: z.number().optional(),
  actionsOptions: z.string().optional(),
  findingsOptions: z.string().optional(),
  recommendationsOptions: z.string().optional(),
  generatedParagraph: z.string().nullish().optional(),
  generationStatus: z.enum(["draft", "generating", "generated"]).optional(),
  generatedAt: z.coerce.date().nullish().optional(),
  actions: z.array(z.string()).optional(),
  findings: z.array(z.string()).optional(),
  recommendations: z.array(z.string()).optional(),
  notes: z.string().optional(),
  completed: z.boolean().optional(),
});

export type UpdateSurveyItemInput = z.infer<typeof updateSurveyItemInputSchema>;

export const updateSurveyItemResponseSchema = z.object({
  item: surveyItemSchema,
});

export type UpdateSurveyItemResponse = z.infer<
  typeof updateSurveyItemResponseSchema
>;

export const generateSurveyItemParagraphInputSchema = z.object({
  surveyId: z.string(),
  itemId: z.string(),
});

export type GenerateSurveyItemParagraphInput = z.infer<
  typeof generateSurveyItemParagraphInputSchema
>;

export const generateSurveyItemParagraphResponseSchema = z.object({
  item: surveyItemSchema,
});

export type GenerateSurveyItemParagraphResponse = z.infer<
  typeof generateSurveyItemParagraphResponseSchema
>;

export const updateSurveyNoteInputSchema = z.object({
  surveyId: z.string(),
  noteId: z.string(),
  actions: z.string().optional(),
  findings: z.string().optional(),
  notes: z.string().optional(),
  recommendations: z.string().optional(),
  recUrgency: z.string().optional(),
  aiParagraph: z.string().optional(),
});

export const updateSurveyAreaInputSchema = z.object({
  surveyId: z.string(),
  areaId: z.string(),
  name: z.string().optional(),
  sortOrder: z.number().optional(),
});

export type UpdateSurveyAreaInput = z.infer<typeof updateSurveyAreaInputSchema>;

export const updateSurveyAreaResponseSchema = z.object({
  area: surveyAreaSchema,
});

export type UpdateSurveyAreaResponse = z.infer<
  typeof updateSurveyAreaResponseSchema
>;

export type UpdateSurveyNoteInput = z.infer<typeof updateSurveyNoteInputSchema>;

export const updateSurveyNoteResponseSchema = z.object({
  note: surveyNoteSchema,
});

export type UpdateSurveyNoteResponse = z.infer<
  typeof updateSurveyNoteResponseSchema
>;

export const createSurveyAttachmentInputSchema = z.object({
  surveyId: z.string(),
  surveyItemId: z.string(),
  fileKey: z.string(),
  filename: z.string(),
  contentType: z.string().optional(),
  size: z.coerce.number().optional(),
});

export type CreateSurveyAttachmentInput = z.infer<
  typeof createSurveyAttachmentInputSchema
>;

export const createSurveyAttachmentResponseSchema = z.object({
  attachment: attachmentSchema,
});

export type CreateSurveyAttachmentResponse = z.infer<
  typeof createSurveyAttachmentResponseSchema
>;

export const generateSurveyReportInputSchema = z.object({
  surveyId: z.string(),
  templateId: z.string().optional(),
});

export type GenerateSurveyReportInput = z.infer<
  typeof generateSurveyReportInputSchema
>;

export const generateSurveyReportResponseSchema = z.object({
  jobId: z.string(),
  status: z.enum(["queued", "running", "complete", "failed"]),
});

export type GenerateSurveyReportResponse = z.infer<
  typeof generateSurveyReportResponseSchema
>;

export const surveyExportStatusSchema = z.object({
  jobId: z.string(),
  status: z.string(),
  outputFileKey: z.string().nullish(),
});

export type SurveyExportStatus = z.infer<typeof surveyExportStatusSchema>;

export const exportNotesResponseSchema = z.object({
  payload: z.record(z.string(), z.unknown()),
});

export type ExportNotesResponse = z.infer<typeof exportNotesResponseSchema>;
